import Shader from "_src/engine/shader";
import Uniform from "_src/engine/uniform";
import { uniformType, uniformSource } from "_src/engine/constants";
import { vec2, vec3, vec4, mat4 } from "_src/libs/gl-matrix";

import vertText from "_src/shaders/line/line.glslv";
import fragText from "_src/shaders/line/line.glslf";
import {lineColorType, lineType} from "_src/constants";

const _VEC2_TMP = vec2.create();

const preprocessorRules = {
  "SCREEN": lineType.SCREEN,
  "INSTANCED": lineType.INSTANCED,
  "GENERAL": lineType.GENERAL,
  "UNIFORM": lineColorType.UNIFORM,
  "TEXTURE": lineColorType.TEXTURE,
  "BUFFER": lineColorType.BUFFER,
  "OUTPUT": lineColorType.OUTPUT,
};

export default class LineShader extends Shader {

  constructor(params = {}) {

    super(params);

    this.animated = false;
    this.instanceDivisor = 1;
    this.instancedAttributesRemapping = this.instancedMode ? {
      "a_line_start": "a_position",
      "a_line_direction": "a_normal"
    } : {};

    this.attachUniforms(params);

  }

  //reimplementation
  initDirectives(params) {
    this._directives["COLOR_TYPE"] = params.colorType !== undefined ? _lineColorTypeString(params.colorType) : "UNIFORM";
    this._directives["FADING"] = params.useFading !== undefined ? params.useFading : false;
    this._directives["ANTIALIASING"] = params.antialiasing !== undefined ? params.antialiasing : true;

    if (this.instancedMode) {
      this._directives["LINE_TYPE"] = "INSTANCED";
    } else if (params.screenSpace) {
      this._directives["LINE_TYPE"] = "SCREEN";
    } else {
      this._directives["LINE_TYPE"] = "GENERAL";
    }

    this._dependentDirectives.add("LINE_TYPE");
  }

  compile() {
    super.compile(vertText, fragText, preprocessorRules);
  }

  updateObjectUniformValues(object)   {
    mat4.copy(this.uniforms["u_model_matrix"].value, object.modelMatrix);
    if (!this.instancedMode) {
      vec4.copy(this.uniforms["u_diffuse"].value, object.properties.diffuseColor);
    }
  }

  updateSceneUniformValues(scene)   {

    const camera = scene.camera;

    mat4.copy(this.uniforms["u_view_mat"].value, camera.viewMat);
    mat4.copy(this.uniforms["u_proj_mat"].value, camera.projMat);
    mat4.copy(this.uniforms["u_view_inv_mat"].value, camera.modelMatrix);
    vec2.copy(this.uniforms["u_viewport"].value, vec2.set(_VEC2_TMP, camera.viewport[2], camera.viewport[3]));
    vec2.copy(this.uniforms["u_cam_near_far"].value, camera.nearFar);

  }

  attachUniforms(params) {

    const lineWidth         = "lineWidth" in params ? params.lineWidth : 2;
    const lineLength        = "lineLength" in params ? params.lineLength : 1;
    const texPeriod         = "texPeriod" in params ? params.lineWidth : 0.2;
    const texOffset         = 0.0;
    const modelMatrix       = mat4.create();
    const viewMatrix        = mat4.create();
    const projectionMatrix  = mat4.create();
    const viewInverseMatrix = mat4.create();
    const diffuseColor      = "diffuseColor" in params ? params.diffuseColor : vec4.create();
    const viewport          = vec2.fromValues(100.0, 100.0);
    const fadeDistance      = "fadeDistance" in params ? params.fadeDistance : 100;
    const viewTarget        = "viewTarget" in params ? vec3.clone(params.viewTarget)
                                                     : vec3.create();
    const distanceRange     = "distanceRange" in params ? vec2.clone(params.distanceRange) : vec2.create();
    const viewPoint         = params.viewPoint ? vec3.clone(params.viewPoint)
                                               : vec3.create();
    const tolerance         = "tolerance" in params ? params.tolerance : 1.0;

    this.uniforms["u_view_mat"]          = new Uniform(uniformType.MAT4, viewMatrix, uniformSource.SCENE);
    this.uniforms["u_proj_mat"]          = new Uniform(uniformType.MAT4, projectionMatrix, uniformSource.SCENE);
    this.uniforms["u_cam_near_far"]      = new Uniform(uniformType.FLOAT2,  vec2.create(), uniformSource.SCENE);
    this.uniforms["u_view_inv_mat"]      = new Uniform(uniformType.MAT4, viewInverseMatrix, uniformSource.SCENE);
    this.uniforms["u_viewport"]          = new Uniform(uniformType.FLOAT2, viewport, uniformSource.SCENE);
    this.uniforms["u_model_matrix"]      = new Uniform(uniformType.MAT4, modelMatrix, uniformSource.OBJECT);
    this.uniforms["u_diffuse"]           = new Uniform(uniformType.FLOAT4, diffuseColor, this.instancedMode ? uniformSource.SHADER : uniformSource.OBJECT);
    this.uniforms["u_tex_offset"]        = new Uniform(uniformType.FLOAT1, texOffset);
    this.uniforms["u_line_width"]        = new Uniform(uniformType.FLOAT1, lineWidth);
    this.uniforms["u_line_length"]       = new Uniform(uniformType.FLOAT1, lineLength);
    this.uniforms["u_tex_period_length"] = new Uniform(uniformType.FLOAT1, texPeriod);
    this.uniforms["u_fade_distance"]     = new Uniform(uniformType.FLOAT1, fadeDistance);
    this.uniforms["u_view_point"]        = new Uniform(uniformType.FLOAT3, viewPoint);
    this.uniforms["u_view_target"]       = new Uniform(uniformType.FLOAT3, viewTarget);
    this.uniforms["tDiffuse"]            = new Uniform(uniformType.INT1, 0);
    this.uniforms["u_tolerance"]         = new Uniform(uniformType.FLOAT1, tolerance);
    this.uniforms["u_distance_range"]    = new Uniform(uniformType.FLOAT2, distanceRange);
  }

  setTexturePeriod(value) {
    this.setUniform("u_tex_period_length", value);
  }

  setLineLength(value) {
    this.setUniform("u_line_length", value);
  }

  setLineWidth(value) {
    this.setUniform("u_line_width", value);
  }

  setViewPoint(point) {
    this.setUniform("u_view_point", point);
  }

  setViewTarget(point) {
    this.setUniform("u_view_target", point);
  }

  setFadeDistance(dist) {
    this.setUniform("u_fade_distance", dist);
  }

  getTetxureOffset() {
    return this.uniforms["u_tex_offset"].value;
  }

  setTextureOffset(value) {
    this.setUniform("u_tex_offset", value);
  }

}

function _lineColorTypeString(colorType) {
  switch (colorType) {
    case lineColorType.UNIFORM:
      return "UNIFORM";
    case lineColorType.TEXTURE:
      return "TEXTURE";
    case lineColorType.BUFFER:
      return "BUFFER";
    case lineColorType.OUTPUT:
      return "OUTPUT";
  }
}
